import { motion } from 'framer-motion';
import type { DayPlan } from '@/lib/index';
import { mealTypeConfig } from '@/data/index';

interface DayCardProps {
  plan: DayPlan;
  isSelected: boolean;
  onClick: () => void;
  index: number;
}

export function DayCard({ plan, isSelected, onClick, index }: DayCardProps) {
  return (
    <motion.button
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, type: 'spring', stiffness: 300, damping: 30 }}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      className={`
        relative w-full text-left p-4 rounded-2xl border transition-all duration-200 cursor-pointer
        ${isSelected
          ? 'bg-primary/20 border-primary shadow-lg shadow-primary/20'
          : 'bg-card border-border hover:border-primary/50 hover:bg-primary/5'
        }
      `}
    >
      <div className="flex flex-col items-center gap-2">
        <div className={`
          w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold
          ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}
        `}>
          {plan.dayNum}
        </div>
        <div className="text-center">
          <div className={`text-sm font-semibold ${isSelected ? 'text-primary' : 'text-foreground'}`}>
            {plan.day}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {plan.meals.length} meals
          </div>
        </div>
      </div>
      {isSelected && (
        <motion.div
          layoutId="day-indicator"
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-1 bg-primary rounded-full"
        />
      )}
    </motion.button>
  );
}

interface MealCardProps {
  meal: DayPlan['meals'][0];
  index: number;
}

export function MealCard({ meal, index }: MealCardProps) {
  const config = mealTypeConfig[meal.type];

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.08, type: 'spring', stiffness: 300, damping: 30 }}
      className={`
        rounded-2xl border p-5 ${config.bg} ${config.border}
      `}
      style={{
        boxShadow: '0 4px 20px -4px rgba(0,0,0,0.3)',
      }}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-2.5 h-2.5 rounded-full ${config.dot} flex-shrink-0`} />
        <span className="text-xl">{meal.emoji}</span>
        <h3 className={`font-bold text-base ${config.color}`}>{meal.label}</h3>
      </div>
      <div className="space-y-2">
        {meal.items.map((item, i) => (
          <div key={i} className="flex items-center justify-between gap-4">
            <span className="text-sm text-foreground/90 font-medium">{item.name}</span>
            <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded-lg ${config.bg} ${config.color} whitespace-nowrap`}>
              {item.amount}
            </span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

interface SummaryStripProps {
  plan: DayPlan;
}

export function SummaryStrip({ plan }: SummaryStripProps) {
  const mealCounts = {
    breakfast: plan.meals.filter(m => m.type === 'breakfast').length,
    preWorkout: plan.meals.filter(m => m.type === 'pre-workout').length,
    postWorkout: plan.meals.filter(m => m.type === 'post-workout').length,
    snacks: plan.meals.filter(m => m.type === 'snack').length,
    dinner: plan.meals.filter(m => m.type === 'dinner').length,
  };

  const stats = [
    { label: 'Total Meals', value: plan.meals.length, icon: '🍽️', color: 'text-primary' },
    { label: 'Pre-Workout', value: mealCounts.preWorkout ? '✓' : '—', icon: '⚡', color: 'text-orange-400' },
    { label: 'Post-Workout', value: mealCounts.postWorkout ? '✓' : '—', icon: '💪', color: 'text-green-400' },
    { label: 'Snacks', value: mealCounts.snacks || '—', icon: '🥜', color: 'text-purple-400' },
  ];

  return (
    <div className="grid grid-cols-4 gap-3 mb-6">
      {stats.map((stat, i) => (
        <motion.div
          key={stat.label}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.05, type: 'spring', stiffness: 300, damping: 30 }}
          className="bg-card border border-border rounded-xl p-3 text-center"
        >
          <div className="text-xl mb-1">{stat.icon}</div>
          <div className={`text-lg font-bold ${stat.color}`}>{stat.value}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{stat.label}</div>
        </motion.div>
      ))}
    </div>
  );
}
